import React from "react";
import MainLogo from "./MainLogo";
import MicroIcon from "./MicroIcon";
import SignatureRW from "./SignatureRW";
import { COLORS, TAGLINE, WALK_TAGLINE, PILLARS } from "./tokens";

/**
 * Banners — pre-sized SVGs for Etsy / Shopify / YouTube / Instagram.
 * All inherit the Rona Wellness palette.
 */

const Textured = ({ id, base }) => (
  <defs>
    <linearGradient id={id + "-bg"} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor={COLORS.creamSoft} />
      <stop offset="55%" stopColor={base} stopOpacity="0.35" />
      <stop offset="100%" stopColor={base} stopOpacity="0.65" />
    </linearGradient>
    <radialGradient id={id + "-glow"} cx="20%" cy="30%" r="60%">
      <stop offset="0%" stopColor={COLORS.yellowSoft} stopOpacity="0.7" />
      <stop offset="100%" stopColor={COLORS.yellowSoft} stopOpacity="0" />
    </radialGradient>
  </defs>
);

/* ---------------- ETSY 3360 x 840 ---------------- */
export const EtsyBanner = React.forwardRef(function EtsyBanner(
  { width = 840, id = "banner-etsy" },
  ref
) {
  const ratio = 840 / 3360;
  return (
    <svg
      ref={ref}
      id={id}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 3360 840"
      width={width}
      height={width * ratio}
      role="img"
      aria-label="Etsy banner"
    >
      <Textured id={id} base={COLORS.dustyBlue} />
      <rect width="3360" height="840" fill={`url(#${id}-bg)`} />
      <rect width="3360" height="840" fill={`url(#${id}-glow)`} />
      {/* decorative horizon line */}
      <path d="M0 620 Q 1680 560 3360 620" stroke={COLORS.gold} strokeOpacity="0.35" strokeWidth="1.2" fill="none" />

      {/* Main logo */}
      <g transform="translate(260,170)">
        <MainLogo size={500} />
      </g>

      {/* Wordmark + tagline */}
      <g transform="translate(900,300)">
        <text fontFamily="'Cormorant Garamond', serif" fontSize="180" fill={COLORS.goldDeep} letterSpacing="8" fontWeight="500">Rona Wellness</text>
        <text y="120" fontFamily="'Marcellus', serif" fontSize="46" fill={COLORS.inkSoft} letterSpacing="14">{PILLARS.toUpperCase()}</text>
        <text y="210" fontFamily="'Marcellus', serif" fontSize="40" fill={COLORS.brown} letterSpacing="10" fontStyle="italic">{TAGLINE}</text>
      </g>

      {/* Signature mark top right */}
      <g transform="translate(2940,90)">
        <SignatureRW size={320} color={COLORS.gold} />
      </g>
    </svg>
  );
});

/* ---------------- SHOPIFY HERO 1920 x 1080 ---------------- */
export const ShopifyHero = React.forwardRef(function ShopifyHero(
  { width = 720, id = "banner-shopify" },
  ref
) {
  const ratio = 1080 / 1920;
  return (
    <svg
      ref={ref}
      id={id}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1920 1080"
      width={width}
      height={width * ratio}
      role="img"
      aria-label="Shopify hero banner"
    >
      <Textured id={id} base={COLORS.sage} />
      <rect width="1920" height="1080" fill={`url(#${id}-bg)`} />
      <rect width="1920" height="1080" fill={`url(#${id}-glow)`} />

      <g transform="translate(180,260)">
        <MainLogo size={560} />
      </g>

      <g transform="translate(860,360)">
        <text fontFamily="'Cormorant Garamond', serif" fontSize="150" fill={COLORS.brownDeep} letterSpacing="4" fontWeight="500">Rona Wellness</text>
        <text y="110" fontFamily="'Marcellus', serif" fontSize="40" fill={COLORS.sageDeep} letterSpacing="12">{PILLARS.toUpperCase()}</text>
        <line x1="0" y1="160" x2="180" y2="160" stroke={COLORS.gold} strokeWidth="1.4" />
        <text y="220" fontFamily="'Marcellus', serif" fontSize="34" fill={COLORS.inkSoft} letterSpacing="8" fontStyle="italic">{TAGLINE}</text>
        <text y="300" fontFamily="'Marcellus', serif" fontSize="22" fill={COLORS.brown} letterSpacing="10">SPIRITUAL & SELF-CARE WELLNESS</text>
      </g>
    </svg>
  );
});

/* ---------------- YOUTUBE 2560 x 1440 ---------------- */
export const YouTubeBanner = React.forwardRef(function YouTubeBanner(
  { width = 720, id = "banner-youtube" },
  ref
) {
  const ratio = 1440 / 2560;
  return (
    <svg
      ref={ref}
      id={id}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 2560 1440"
      width={width}
      height={width * ratio}
      role="img"
      aria-label="YouTube banner"
    >
      <Textured id={id} base={COLORS.dustyBlue} />
      <rect width="2560" height="1440" fill={`url(#${id}-bg)`} />
      <rect width="2560" height="1440" fill={`url(#${id}-glow)`} />

      {/* Safe-area centered content for YT */}
      <g transform="translate(900,420)">
        <MainLogo size={500} />
      </g>
      <g transform="translate(1280,1020)" textAnchor="middle">
        <text textAnchor="middle" fontFamily="'Cormorant Garamond', serif" fontSize="110" fill={COLORS.goldDeep} letterSpacing="6" fontWeight="500">Rona Wellness</text>
        <text textAnchor="middle" y="70" fontFamily="'Marcellus', serif" fontSize="34" fill={COLORS.inkSoft} letterSpacing="18">{PILLARS.toUpperCase()}</text>
        <text textAnchor="middle" y="140" fontFamily="'Marcellus', serif" fontSize="30" fill={COLORS.brown} letterSpacing="8" fontStyle="italic">{TAGLINE}</text>
      </g>
    </svg>
  );
});

/* ---------------- INSTAGRAM STORY 1080 x 1920 ---------------- */
export const InstagramStory = React.forwardRef(function InstagramStory(
  { width = 320, id = "banner-instagram-story" },
  ref
) {
  const ratio = 1920 / 1080;
  return (
    <svg
      ref={ref}
      id={id}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1080 1920"
      width={width}
      height={width * ratio}
      role="img"
      aria-label="Instagram story"
    >
      <Textured id={id} base={COLORS.dustyBlue} />
      <rect width="1080" height="1920" fill={`url(#${id}-bg)`} />
      <rect width="1080" height="1920" fill={`url(#${id}-glow)`} />

      {/* thin gold frame inside safe area */}
      <rect x="60" y="260" width="960" height="1420" fill="none" stroke={COLORS.gold} strokeOpacity="0.55" strokeWidth="1.4" />
      <rect x="76" y="276" width="928" height="1388" fill="none" stroke={COLORS.gold} strokeOpacity="0.25" strokeWidth="0.8" />

      <g transform="translate(290,420)">
        <MainLogo size={500} />
      </g>

      <g transform="translate(540,1060)" textAnchor="middle">
        <text textAnchor="middle" fontFamily="'Cormorant Garamond', serif" fontSize="96" fill={COLORS.brownDeep} letterSpacing="4" fontWeight="500">Rona Wellness</text>
        <line x1="-170" y1="36" x2="170" y2="36" stroke={COLORS.gold} strokeWidth="1" />
        <text textAnchor="middle" y="92" fontFamily="'Marcellus', serif" fontSize="28" fill={COLORS.sageDeep} letterSpacing="14">{PILLARS.toUpperCase()}</text>
        <text textAnchor="middle" y="170" fontFamily="'Marcellus', serif" fontSize="30" fill={COLORS.inkSoft} letterSpacing="4" fontStyle="italic">{TAGLINE}</text>
      </g>

      <g transform="translate(490,1520)">
        <MicroIcon size={100} color={COLORS.sage} />
      </g>
    </svg>
  );
});

/* ---------------- FACEBOOK COVER 1640 x 924 ---------------- */
export const FacebookCover = React.forwardRef(function FacebookCover(
  { width = 720, id = "banner-facebook" },
  ref
) {
  const ratio = 924 / 1640;
  return (
    <svg
      ref={ref}
      id={id}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1640 924"
      width={width}
      height={width * ratio}
      role="img"
      aria-label="Facebook cover"
    >
      <Textured id={id} base={COLORS.sage} />
      <rect width="1640" height="924" fill={`url(#${id}-bg)`} />
      <rect width="1640" height="924" fill={`url(#${id}-glow)`} />

      {/* Safe zone: desktop crops top/bottom; mobile shows full. Keep content center-ish. */}
      <g transform="translate(140,210)">
        <MainLogo size={500} />
      </g>

      <g transform="translate(740,340)">
        <text fontFamily="'Cormorant Garamond', serif" fontSize="130" fill={COLORS.brownDeep} letterSpacing="4" fontWeight="500">Rona Wellness</text>
        <text y="92" fontFamily="'Marcellus', serif" fontSize="34" fill={COLORS.sageDeep} letterSpacing="12">{PILLARS.toUpperCase()}</text>
        <line x1="0" y1="140" x2="160" y2="140" stroke={COLORS.gold} strokeWidth="1.4" />
        <text y="198" fontFamily="'Marcellus', serif" fontSize="30" fill={COLORS.inkSoft} letterSpacing="6" fontStyle="italic">{TAGLINE}</text>
      </g>
    </svg>
  );
});

/* ---------------- YOUTUBE THUMBNAIL 1280 x 720 ---------------- */
export const YouTubeThumbnail = React.forwardRef(function YouTubeThumbnail(
  { width = 520, id = "banner-youtube-thumb" },
  ref
) {
  const ratio = 720 / 1280;
  return (
    <svg
      ref={ref}
      id={id}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1280 720"
      width={width}
      height={width * ratio}
      role="img"
      aria-label="YouTube thumbnail"
    >
      <Textured id={id} base={COLORS.cream} />
      <rect width="1280" height="720" fill={COLORS.creamSoft} />
      <rect width="1280" height="720" fill={`url(#${id}-glow)`} />

      {/* thin gold border */}
      <rect x="28" y="28" width="1224" height="664" fill="none" stroke={COLORS.gold} strokeWidth="1.5" />

      <g transform="translate(90,120)">
        <MainLogo size={480} />
      </g>

      <g transform="translate(640,260)">
        <text fontFamily="'Cormorant Garamond', serif" fontSize="120" fill={COLORS.brownDeep} letterSpacing="3" fontWeight="500">Rona Walk</text>
        <text y="78" fontFamily="'Cormorant Garamond', serif" fontSize="84" fill={COLORS.goldDeep} letterSpacing="2" fontStyle="italic">Morning Ritual</text>
        <line x1="0" y1="122" x2="140" y2="122" stroke={COLORS.gold} strokeWidth="1.4" />
        <text y="176" fontFamily="'Marcellus', serif" fontSize="26" fill={COLORS.inkSoft} letterSpacing="8" fontStyle="italic">{WALK_TAGLINE}</text>
      </g>
    </svg>
  );
});

/* ---------------- INSTAGRAM POST 1080 x 1080 ---------------- */
export const InstagramPost = React.forwardRef(function InstagramPost(
  { width = 480, id = "banner-instagram" },
  ref
) {
  return (
    <svg
      ref={ref}
      id={id}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1080 1080"
      width={width}
      height={width}
      role="img"
      aria-label="Instagram square post"
    >
      <Textured id={id} base={COLORS.cream} />
      <rect width="1080" height="1080" fill={COLORS.creamSoft} />
      <rect width="1080" height="1080" fill={`url(#${id}-glow)`} />

      {/* thin gold frame */}
      <rect x="40" y="40" width="1000" height="1000" fill="none" stroke={COLORS.gold} strokeWidth="1.5" />
      <rect x="56" y="56" width="968" height="968" fill="none" stroke={COLORS.gold} strokeOpacity="0.35" strokeWidth="0.8" />

      <g transform="translate(290,140)">
        <MainLogo size={500} />
      </g>
      <g transform="translate(540,760)" textAnchor="middle">
        <text textAnchor="middle" fontFamily="'Cormorant Garamond', serif" fontSize="92" fill={COLORS.brownDeep} letterSpacing="4" fontWeight="500">Rona Wellness</text>
        <line x1="-160" y1="32" x2="160" y2="32" stroke={COLORS.gold} strokeWidth="1" />
        <text textAnchor="middle" y="78" fontFamily="'Marcellus', serif" fontSize="26" fill={COLORS.sageDeep} letterSpacing="14">{PILLARS.toUpperCase()}</text>
        <text textAnchor="middle" y="140" fontFamily="'Marcellus', serif" fontSize="24" fill={COLORS.inkSoft} letterSpacing="6" fontStyle="italic">{TAGLINE}</text>
      </g>
      <g transform="translate(490,960)">
        <MicroIcon size={100} color={COLORS.sage} />
      </g>
    </svg>
  );
});
